import compressImage from "browser-image-compression";
import { useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";

import { EditButtonProps, SaveButton, SaveButtonProps } from "@refinedev/antd";
import { Col, Form, Input, InputRef, Row, message } from "antd";
import classes from "./DocumentEditor.module.css";

const convertToBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);

    fileReader.onload = () => {
      resolve(fileReader.result);
    };

    fileReader.onerror = (error) => {
      reject(error);
    };
  });

export const isValidQuill = (value: string | undefined | null) => {
  if (
    (value || "")
      .toString()
      .replace(/<(.|\n)*?>/g, "")
      .trim().length === 0
  ) {
    return false;
  } else {
    return true;
  }
};

export const DocumentEditor: React.FC<{
  title: string;
  content: string;
  actionButtonProps: EditButtonProps | SaveButtonProps;
}> = ({ title, content, actionButtonProps }) => {
  const [messageApi, contextHolder] = message.useMessage();

  const [disabled, setDisabled] = useState(true);
  const [loading, setLoading] = useState(false);

  const titleRef = useRef<InputRef | null>(null);
  const contentRef = useRef(content);
  const quillRef = useRef<ReactQuill>(null);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [
            { header: [1, 2, 3, 4, 5, 6, false] },
            "bold",
            "italic",
            "underline",
            "strike",
            "link",
            "blockquote",
            "code-block",
            { list: "ordered" },
            { list: "bullet" },
            { script: "sub" },
            { script: "super" },
            { indent: "-1" },
            { indent: "+1" },
            { color: [] },
            { background: [] },
            { align: [] },
            "image"
          ],
          ["clean"]
        ],
        handlers: {
          image: () => {
            const input = document.createElement("input");
            input.setAttribute("type", "file");
            input.setAttribute("accept", "image/png, image/jpg, image/jpeg");
            input.click();
            input.onchange = async () => {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              const file = input.files[0];
              console.log(file.type);
              if (
                !(
                  file.type === "image/jpg" ||
                  file.type === "image/jpeg" ||
                  file.type === "image/png"
                )
              ) {
                message.error("Only JPG and PNG images are allowed");
                return;
              }

              // check if file size is greater than 5mb
              if (file.size > 5242880) {
                message.error("File size is too big! File size should be less than 5mb");
                return;
              }

              messageApi.open({
                type: "loading",
                content: "Uploading image...",
                key: "uploading",
                duration: 0,
                className: classes.message
              });

              // compress file then convert to base 64
              const compressedFile = await compressImage(file, {
                maxSizeMB: 1,
                maxWidthOrHeight: 1920,
                useWebWorker: true
              });

              // const url = await uploadImage(compressedFile);
              const base64 = await convertToBase64(compressedFile);

              messageApi.destroy("uploading");

              const range = quillRef.current?.getEditor().getSelection();
              quillRef.current?.getEditor().insertEmbed(range?.index as number, "image", base64);
            };
          }
        }
      }
    }),
    []
  );
  return (
    <Row>
      <Col span={24} style={{ marginBottom: 24 }}>
        <>
          <Form.Item
            label="Title"
            normalize={(value) => value?.trimStart()}
            name={"title"}
            rules={[
              {
                validator: () => {
                  const titleLength = titleRef.current?.input?.value.trim().length;
                  if (!titleLength) {
                    setDisabled(true);
                    return Promise.reject("This field is required.");
                  }

                  if (titleLength < 3) {
                    setDisabled(true);
                    return Promise.reject("Title should be at least 3 characters.");
                  }

                  if (titleLength > 255) {
                    setDisabled(true);
                    return Promise.reject("Title should be less than 256 characters.");
                  }

                  setDisabled(!isValidQuill(contentRef.current));
                  return Promise.resolve();
                }
              }
            ]}>
            <Input
              ref={titleRef}
              type="text"
              id="title"
              defaultValue={title}
              style={{
                width: "100%",
                outline: "none",
                backgroundColor: "transparent",
                border: "2px solid #FF5A17",
                color: "white"
              }}
            />
          </Form.Item>
        </>
      </Col>
      {contextHolder}
      <Col span={24}>
        <Form.Item name="content">
          <ReactQuill
            ref={quillRef}
            value={contentRef.current}
            className={classes.editor}
            onChange={(content, _, source) => {
              contentRef.current = content;
              if (source === "user") {
                if (
                  (titleRef.current?.input?.value?.trim() || "").length >= 3 &&
                  (titleRef.current?.input?.value?.trim() || "").length <= 255 &&
                  isValidQuill(content)
                ) {
                  setDisabled(false);
                } else {
                  setDisabled(true);
                }
              }
            }}
            theme="snow"
            modules={modules}
            style={{
              backgroundColor: "#1A1D38",
              border: "1px solid #FF5A17",
              width: "100%"
            }}
          />
        </Form.Item>
      </Col>
      <span className={classes.helpText}>*Image size should be less than 5mb</span>
      <Col span={24}>
        <SaveButton
          {...actionButtonProps}
          onClick={(e) => {
            setLoading(true);
            actionButtonProps.onClick?.(e);
          }}
          loading={loading}
          className={classes.submitBtn}
          disabled={disabled}>
          Save changes
        </SaveButton>
      </Col>
    </Row>
  );
};
